/**
 * Opening and closing on the motion engine (redesign §35): an element enters
 * from `from` to rest on a spring, and on close it goes to `exit` in a 120 ms
 * tween before it is unmounted. Reopened while it is still leaving, it turns
 * back from where it is on screen and stays mounted.
 *
 * `useMotionMount` serves what its caller mounts and unmounts itself (a modal,
 * a side panel, a menu): it enters on mount, and on unmount a copy of the
 * element is left where it stood, inert and hidden from assistive technology,
 * to play the exit and then be removed — so no caller has to keep a closing
 * thing mounted.
 *
 * Under reduced motion the engine itself holds every move at its end and
 * fades the opacity in 200 ms (§35, rule 57), so nothing here checks it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { set, to, type Props, type SpringOptions } from './motion';

const REST: Required<Props> = { o: 1, x: 0, y: 0, s: 1, sx: 1, sy: 1 };

/** The rest value of each key `from` moves, which is where an entrance ends. */
export function restOf(from: Props): Props {
  const out: Props = {};
  for (const key of Object.keys(from) as (keyof Props)[]) out[key] = REST[key];
  return out;
}

export function usePresence<T extends HTMLElement>(
  open: boolean,
  from: Props,
  enter: SpringOptions,
  exit: Props
): { mounted: boolean; ref: (node: T | null) => void } {
  const [mounted, setMounted] = useState(open);
  const node = useRef<T | null>(null);
  const openNow = useRef(open);
  const spec = useRef({ from, enter, exit });
  spec.current = { from, enter, exit };

  useEffect(() => {
    openNow.current = open;
    if (open) {
      setMounted(true);
      if (node.current) to(node.current, restOf(spec.current.from), spec.current.enter);
      return;
    }
    if (!node.current) {
      setMounted(false);
      return;
    }
    to(node.current, spec.current.exit, {
      tw: 0.12,
      done: () => {
        if (!openNow.current) setMounted(false);
      }
    });
  }, [open]);

  const ref = useCallback((el: T | null) => {
    node.current = el;
    if (!el) return;
    set(el, spec.current.from);
    to(el, restOf(spec.current.from), spec.current.enter);
  }, []);

  return { mounted: mounted || open, ref };
}

/** Plays `exit` on a copy of `el` left where it stood, then removes the copy. */
function leave(el: HTMLElement, exit: Props): void {
  if (!el.isConnected) return;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return;
  const layer = getComputedStyle(el).zIndex;
  const ghost = el.cloneNode(true) as HTMLElement;
  ghost.removeAttribute('id');
  ghost.setAttribute('aria-hidden', 'true');
  ghost.inert = true;
  Object.assign(ghost.style, {
    position: 'fixed',
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    right: 'auto',
    bottom: 'auto',
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: '0',
    pointerEvents: 'none',
    zIndex: layer === 'auto' ? '30' : layer
  });
  document.body.appendChild(ghost);
  const gone = (): void => ghost.remove();
  to(ghost, exit, { tw: 0.12, done: gone });
  window.setTimeout(gone, 600);
}

/**
 * A ref for an element its caller mounts and unmounts: it enters from `from`
 * on `enter`, and with an `exit` a copy plays that exit in 120 ms once it is gone.
 */
export function useMotionMount<T extends HTMLElement>(
  from: Props,
  enter: SpringOptions,
  exit?: Props
): (node: T | null) => void {
  const spec = useRef({ from, enter, exit });
  spec.current = { from, enter, exit };
  const held = useRef<T | null>(null);
  return useCallback((el: T | null) => {
    if (el) {
      held.current = el;
      set(el, spec.current.from);
      to(el, restOf(spec.current.from), spec.current.enter);
      return;
    }
    const was = held.current;
    held.current = null;
    if (was && spec.current.exit) leave(was, spec.current.exit);
  }, []);
}
